import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { ProgramDocumentDetailsResponse } from "../../../../../../api/services/program/models";
import {
  useDeleteProgramDocumentMutation,
  useGetProgramDocumentsList,
} from "../../../../../../api/services/program/programApis";
import { ReactComponent as UploadIcon } from "../../../../../../assets/images/upload.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiLink,
  MuiNotesToolTip,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  TableHeaderSearchIcon,
  WarningConfirmationModal,
} from "../../../../../../components";
import { ButtonWithIcon } from "../../../../../../components/buttonWithIcon/ButtonWithIcon";
import MuiEllipseLabel from "../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  buildActionMenuItems,
  convertToUserFormat,
  convertToUTCFormat,
  filterListByFilterKeyValueAdvanced,
  getFullName,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { programDocumentsTabSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { UploadNewDocumentModal } from "./components/uploadNewDocumentModal/UploadNewDocumentModal";
import { ViewDocumentModal } from "./components/viewDocumentModal/ViewDocumentModal";
import { DocumentsTabWrapper } from "./DocumentsTab.style";
import {
  DocumentsTabFormKeys,
  ProgramDocumentDetailsRowValue,
} from "./models/Models";

interface DocumentsTabProps {
  pageId: string;
  programId: string;
  isViewOnly?: boolean;
  isFromCoachPortal?: boolean;
}

const defaultValues = {
  [DocumentsTabFormKeys.fileName]: "",
  [DocumentsTabFormKeys.type]: "",
  [DocumentsTabFormKeys.description]: "",
  [DocumentsTabFormKeys.uploadedDate]: "",
  [DocumentsTabFormKeys.uploadedBy]: "",
};

export const DocumentsTab: FC<DocumentsTabProps> = ({
  pageId,
  programId,
  isViewOnly = false,
  isFromCoachPortal = false,
}) => {
  const {
    fetchComponentTitle,
    useFetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const { displayToast } = useToast();
  const dispatch = useDispatch();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const {
    mutateAsync: getProgramDocumentsList,
    data: programDocumentsListData,
    isPending: isGetProgramDocumentsListPending,
    isError: isGetProgramDocumentsListError,
    isSuccess: isGetProgramDocumentsListSuccess,
  } = useGetProgramDocumentsList();

  const {
    mutateAsync: deleteProgramDocument,
    isPending: isDeleteProgramDocumentPending,
    isSuccess: isDeleteProgramDocumentSuccess,
    isError: isDeleteProgramDocumentError,
  } = useDeleteProgramDocumentMutation();

  const [rows, setRows] = useState<ProgramDocumentDetailsResponse[]>([]);
  const [updatedRows, setUpdatedRows] = useState<
    ProgramDocumentDetailsResponse[]
  >([]);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [showViewDocumentModal, setShowViewDocumentModal] = useState(false);
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const documentIdRef = useRef<string>();
  const blobIdRef = useRef<string>();
  const [filteredResults, setFilteredResults] = useState<
    ProgramDocumentDetailsRowValue[]
  >([...rows]);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    documentsTitle,
    uploadedNewDocumentsHyperLinkLabel: uploadNewDocumentButton,
    documentNameTableColumn,
    typeTableColumn,
    descriptionTableColumn,
    uploadedDateTableColumn,
    uploadedByTableColumn,
    actionTableColumn,
    noDocumentsAvailableMessage,
    programDocumentTypeList,
    deleteDocumentConfirmationMessage: documentDeleteActionText,
    documentDeleted,
  } = programDocumentsTabSiteCoreKeys;
  const getLabel = useFetchLabel(documentsTitle);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isDocumentListLoading: {
          isVisible:
            isGetProgramDocumentsListPending || isDeleteProgramDocumentPending,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isDocumentListLoading: { isVisible: false } }));
    };
  }, [isGetProgramDocumentsListPending, isDeleteProgramDocumentPending]);

  const callGetDocumentsListAPI = () => {
    getProgramDocumentsList(programId);
  };

  useEffect(() => {
    if (programId && isNotEmpty(programId)) {
      callGetDocumentsListAPI();
    }
  }, [programId]);

  useEffect(() => {
    if (isDeleteProgramDocumentSuccess) {
      displayToast(getLabel(documentDeleted));
      callGetDocumentsListAPI();
    }
  }, [isDeleteProgramDocumentSuccess]);

  useEffect(() => {
    if (isGetProgramDocumentsListSuccess && programDocumentsListData) {
      setRows(programDocumentsListData);
    }
  }, [programDocumentsListData, isGetProgramDocumentsListSuccess]);

  useEffect(() => {
    if (isGetProgramDocumentsListError || isDeleteProgramDocumentError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isGetProgramDocumentsListError, isDeleteProgramDocumentError]);

  const handleViewDocumentClose = () => {
    documentIdRef.current = undefined;
    blobIdRef.current = undefined;
    setShowViewDocumentModal(false);
  };

  const handleDeleteDocumentClose = () => {
    documentIdRef.current = undefined;
    setShowDeleteDocumentModal(false);
  };

  const handleDeleteDocumentConfirmationClick = () => {
    if (documentIdRef.current && isNotEmpty(documentIdRef.current)) {
      setShowDeleteDocumentModal(false);
      deleteProgramDocument(documentIdRef.current);
    }
  };

  useEffect(() => {
    const mappedResults = rows.map((item: ProgramDocumentDetailsRowValue) => {
      return {
        ...item,
        uploadedBy: getFullName(
          item.createdBy?.firstName,
          item.createdBy?.lastName,
          true,
        ),
      };
    });

    reset();
    resetPagination();
    setUpdatedRows([...mappedResults]);
    setFilteredResults([...mappedResults]);
  }, [rows]);

  const { control, reset, getValues } = useForm({
    mode: "all",
    defaultValues: defaultValues,
  });

  const tableColumnFilterWatch = useWatch({ control });

  useEffect(() => {
    if (isNotEmpty(updatedRows)) {
      const filterUploadedDate = tableColumnFilterWatch.uploadedDate
        ? convertToUTCFormat(
            tableColumnFilterWatch.uploadedDate,
            userCountryDateFormat,
          )
        : "";

      const filterList = filterListByFilterKeyValueAdvanced(updatedRows, {
        ...tableColumnFilterWatch,
        uploadedDate: filterUploadedDate,
      }) as ProgramDocumentDetailsRowValue[];
      setFilteredResults([...filterList]);
      resetPagination();
    }
  }, [tableColumnFilterWatch]);

  const handleDocumentUploadSuccess = () => {
    callGetDocumentsListAPI();
    handleCloseUploadDocumentModal();
  };

  const handleCloseUploadDocumentModal = useCallback(
    () => setShowUploadDocumentModal(false),
    [],
  );

  const onUploadDocumentClick = useCallback(() => {
    setShowUploadDocumentModal(true);
  }, []);

  const onViewDocumentClick = (documentId: string, blobId: string) => {
    documentIdRef.current = documentId;
    blobIdRef.current = blobId;
    setShowViewDocumentModal(true);
  };

  const onDeleteDocumentClick = (documentId: string) => {
    documentIdRef.current = documentId;
    setShowDeleteDocumentModal(true);
  };

  const viewPermission = isFromCoachPortal
    ? MODULE_PERMISSIONS.COACH_PORTAL_VIEW_DOCUMENTS
    : MODULE_PERMISSIONS.VIEW_DOCUMENTS;

  const deletePermission = isFromCoachPortal
    ? MODULE_PERMISSIONS.COACH_PORTAL_DELETE_DOCUMENTS
    : MODULE_PERMISSIONS.DELETE_DOCUMENTS;

  const columns = [
    {
      id: DocumentsTabFormKeys.fileName,
      label: getLabel(documentNameTableColumn),
      isSortVisible: true,
      className: "cell-document-name",
      render: (row: RowProps) => {
        if (!row.fileName) return null;

        return hasModulePermission(viewPermission) ? (
          <MuiLink
            data-testid={COMPONENT_IDS.COMMON_TABLE_NAME}
            onClick={() => {
              onViewDocumentClick(row.id, row.blobId);
            }}
          >
            <MuiEllipseLabel
              labelTypoClassName="Link document-name-link"
              text={row.fileName}
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiLink>
        ) : (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {row.fileName}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: DocumentsTabFormKeys.type,
      label: getLabel(typeTableColumn),
      isSortVisible: true,
      className: "cell-min-width",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentsTitle,
              programDocumentTypeList,
              row.type,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: DocumentsTabFormKeys.description,
      label: getLabel(descriptionTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row?.description} />
        </MuiBox>
      ),
    },
    {
      id: DocumentsTabFormKeys.uploadedDate,
      label: getLabel(uploadedDateTableColumn),
      isSortVisible: true,
      className: "cell-min-width",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.uploadedDate
              ? convertToUserFormat(row.uploadedDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: DocumentsTabFormKeys.uploadedBy,
      label: getLabel(uploadedByTableColumn),
      isSortVisible: true,
      className: "cell-min-width",
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {getFullName(
              row.createdBy?.firstName,
              row.createdBy?.lastName,
              true,
            ) || EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(actionTableColumn),
      className: "cell-action",
      render: (row: RowProps) => {
        const documentActions = [
          {
            permissionKey: viewPermission,
            create: () => ({
              onClick: () => onViewDocumentClick(row?.id, row?.blobId),
              dataTestId: "view-document-action",
              label: commonLabels.view,
            }),
          },
        ];

        if (!isViewOnly) {
          documentActions.push({
            permissionKey: deletePermission,
            create: () => ({
              onClick: () => onDeleteDocumentClick(row?.id),
              dataTestId: "delete-document-action",
              label: commonLabels.delete,
            }),
          });
        }

        const menuItems = buildActionMenuItems(
          row,
          documentActions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    renderFilterTextField(DocumentsTabFormKeys.fileName),
    <FormController
      key={DocumentsTabFormKeys.type}
      control={control}
      name={DocumentsTabFormKeys.type}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentsTitle,
          programDocumentTypeList,
          true,
        )}
        showSearch
        value=""
        id={`${pageId}-${DocumentsTabFormKeys.type}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${DocumentsTabFormKeys.uploadedDate}`}
      id={`${pageId}-${DocumentsTabFormKeys.uploadedDate}`}
      name={DocumentsTabFormKeys.uploadedDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(
        getValues(DocumentsTabFormKeys.uploadedDate),
      )}
    />,
    renderFilterTextField(DocumentsTabFormKeys.uploadedBy),
    <></>,
  ];

  const renderHeader = () => {
    return (
      <AccordionHeaderText
        label={fetchComponentTitle(
          programDocumentsTabSiteCoreKeys.documentsTitle,
        )}
      />
    );
  };

  const addDocumentPermission = isFromCoachPortal
    ? MODULE_PERMISSIONS.COACH_PORTAL_ADD_DOCUMENTS
    : MODULE_PERMISSIONS.ADD_DOCUMENTS;

  return (
    <DocumentsTabWrapper>
      <AccordionHeader
        wrapperClassName="documents-accordion"
        title={renderHeader()}
        isExpanded={true}
      >
        <>
          {!isViewOnly && hasModulePermission(addDocumentPermission) && (
            <MuiBox className="semi-header">
              <ButtonWithIcon
                buttonText={getLabel(uploadNewDocumentButton)}
                onActionClick={onUploadDocumentClick}
                ActionIcon={UploadIcon}
              />
            </MuiBox>
          )}

          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            isLastColumnSticky={true}
            tableResults={[...filteredResults]}
            noDataInApi={rows.length === 0}
            noResultMessage={commonLabels.noResult}
            emptyMessage={getLabel(noDocumentsAvailableMessage)}
            isShowDashIfValueEmpty={true}
            priorityResults={[]}
            isFooterVisible={true}
          />
        </>
      </AccordionHeader>

      {showUploadDocumentModal && (
        <UploadNewDocumentModal
          open={showUploadDocumentModal}
          onClose={handleCloseUploadDocumentModal}
          programId={programId}
          onDocumentUploadSuccess={handleDocumentUploadSuccess}
        />
      )}

      {showViewDocumentModal &&
        documentIdRef?.current &&
        blobIdRef?.current && (
          <ViewDocumentModal
            documentId={documentIdRef.current}
            blobId={blobIdRef.current}
            open={showViewDocumentModal}
            onClose={handleViewDocumentClose}
          />
        )}

      {showDeleteDocumentModal && (
        <WarningConfirmationModal
          show={showDeleteDocumentModal}
          onClose={handleDeleteDocumentClose}
          onPrimaryButtonClick={handleDeleteDocumentConfirmationClick}
          labelContent={getLabel(documentDeleteActionText)}
        />
      )}
    </DocumentsTabWrapper>
  );
};
